import React, { useMemo, useState } from 'react';
import { AlertTriangle, Copy, Check, Download } from 'lucide-react';
import { buildReportText } from '../lib/reportText';

/**
 * Compact "Save your report" bar.
 * - Warning that the report is not stored on the server.
 * - Two actions: Copy to Clipboard + Download as .txt (saves a file locally,
 *   so the user never has to leave the tab to paste somewhere safe).
 */
const SaveReportPanel = ({ results, company, audit = null, testIdSuffix = '' }) => {
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const reportText = useMemo(
    () => buildReportText({ results, company, audit }),
    [results, company, audit]
  );

  const idSuffix = testIdSuffix ? `-${testIdSuffix}` : '';

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(reportText);
      } else {
        // Last-resort fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = reportText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = () => {
    try {
      const ticker =
        (results && results.parsed && results.parsed.ticker) ||
        company ||
        'report';
      // Build a filesystem-safe filename: GeoBullBear_TICKER_YYYY-MM-DD-HHMM.txt
      const safeTicker = String(ticker).replace(/[^A-Za-z0-9_-]+/g, '').slice(0, 24);
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
      const filename = `GeoBullBear_${safeTicker}_${stamp}.txt`;

      const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // Defer cleanup to allow Safari to actually trigger the save dialog
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2200);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <section
      data-testid={`save-report-panel${idSuffix}`}
      className="no-print rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3"
      style={{
        borderColor: 'hsl(var(--gb-gold))',
        backgroundColor: 'hsl(var(--gb-surface-2))',
      }}
      aria-labelledby={`save-report-heading${idSuffix}`}
    >
      {/* Warning text */}
      <div className="flex items-start gap-2.5 min-w-0">
        <AlertTriangle
          className="h-5 w-5 flex-shrink-0 mt-0.5"
          style={{ color: 'hsl(var(--gb-gold))' }}
          aria-hidden="true"
        />
        <div className="text-sm leading-snug" style={{ color: 'hsl(var(--gb-text))' }}>
          <span
            id={`save-report-heading${idSuffix}`}
            className="font-semibold"
            style={{ color: 'hsl(var(--gb-navy))' }}
          >
            Save your report.
          </span>{' '}
          <span style={{ color: 'hsl(var(--gb-text-muted))' }}>
            Not stored on our servers — copy or download it now to keep it.
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center flex-shrink-0 gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleDownload}
          data-testid={`download-button${idSuffix}`}
          aria-live="polite"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2"
          style={{
            backgroundColor: downloaded
              ? 'hsl(var(--gb-green-dark))'
              : 'hsl(var(--gb-surface))',
            color: downloaded
              ? 'hsl(var(--gb-primary-foreground))'
              : 'hsl(var(--gb-navy))',
            borderColor: 'hsl(var(--gb-navy))',
          }}
        >
          {downloaded ? (
            <>
              <Check className="h-4 w-4" aria-hidden="true" />
              Downloaded!
            </>
          ) : (
            <>
              <Download className="h-4 w-4" aria-hidden="true" />
              Download .txt
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleCopy}
          data-testid={`copy-button${idSuffix}`}
          aria-live="polite"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2"
          style={{
            backgroundColor: copied
              ? 'hsl(var(--gb-green-dark))'
              : 'hsl(var(--gb-navy))',
            color: 'hsl(var(--gb-primary-foreground))',
          }}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" aria-hidden="true" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" aria-hidden="true" />
              Copy to Clipboard
            </>
          )}
        </button>
      </div>
    </section>
  );
};

export default SaveReportPanel;
